import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';

import { IdService } from '../id.service';
import type { BackendError } from '../../domain/error.domain';

export const REST_FRAMEWORK_CONFIGURATION = new InjectionToken<RestFrameworkConfiguration>('restFrameworkConfiguration');

export interface RestFrameworkConfiguration {
  readonly restApiVersion?: string;
  readonly restConsumerName?: string;
  readonly extraHeaders?: Record<string, string>;
  readonly taiAgentHack?: boolean;
}

/**
 * Adds HTTP headers required by rest framework such as correlation id and consumer name
 */
@Injectable({
  providedIn: 'root'
})
export class RestFrameworkService {
  private readonly configuration: RestFrameworkConfiguration;

  private static readonly HEADER_REST_VERSION: string = 'x-broker-rest-version';
  private static readonly HEADER_REST_CONSUMER: string = 'x-consumer-name';
  private static readonly HEADER_CORRELATION_ID: string = 'x-correlation-id';
  private static readonly DEFAULT_REST_API_VERSION: string = 'v1.0';
  private static readonly DEFAULT_REST_CONSUMER_NAME: string = 'webbroker';
  private static readonly AJAX_REQUEST_INDICATOR_PARAM: string = 'AJAXREQUEST';

  constructor() {
    this.configuration = {
      restApiVersion: RestFrameworkService.DEFAULT_REST_API_VERSION,
      restConsumerName: RestFrameworkService.DEFAULT_REST_CONSUMER_NAME,
      ...(inject<RestFrameworkConfiguration>(REST_FRAMEWORK_CONFIGURATION, { optional: true }) ?? {})
    };
  }

  addHeaders(httpHeaders?: HttpHeaders): HttpHeaders {
    const configuration = this.configuration;
    let headers = httpHeaders !== undefined ? httpHeaders : new HttpHeaders();
    const restApiVersion = configuration.restApiVersion !== undefined ?
      configuration.restApiVersion : RestFrameworkService.DEFAULT_REST_API_VERSION;
    const restConsumerName = configuration.restConsumerName !== undefined ?
      configuration.restConsumerName :
      RestFrameworkService.DEFAULT_REST_CONSUMER_NAME;
    headers = headers
      .set(RestFrameworkService.HEADER_CORRELATION_ID, IdService.getUUID())
      .set(RestFrameworkService.HEADER_REST_VERSION, restApiVersion)
      .set(RestFrameworkService.HEADER_REST_CONSUMER, restConsumerName);

    if (configuration.extraHeaders !== undefined) {
      Object.keys(configuration.extraHeaders).forEach((key: string): void => {
        headers = headers.set(key, configuration.extraHeaders?.[key] ?? '');
      });
    }
    return headers;
  }

  augmentParams(httpParams?: HttpParams): HttpParams {
    const configuration = this.configuration;
    let params = httpParams !== undefined ? httpParams : new HttpParams();

    if (configuration.taiAgentHack === true || configuration.taiAgentHack === undefined) {
      params = params.set(RestFrameworkService.AJAX_REQUEST_INDICATOR_PARAM, '1');
    }
    return params;
  }

  unwrapResponse<T>(response: Record<string, T>): Record<string, T> | T {
    if (response !== undefined && response.payload !== undefined) {
      return response.payload;  // unwrap the envelope and extract payload
    }
    return response;
  }

  getErrors<T>(response: Record<string, T>): Array<BackendError> {
    if (response !== undefined && response.errors !== undefined) {
      return response.errors as Array<BackendError>;
    }
    return [];
  }
}
